import { IClinic, IDoctor } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, FormInstance, Radio } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { gender } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

import { useNotify } from "@utils";
import { FormAction } from "src/Components/FormAction";

interface IProps {
  doctor?: IDoctor;
  callback: (values: IDoctor) => void;
  cancel?: () => void;
  update: (values: IDoctor) => void;
}

const initValue: IDoctor = {
  name: "",
  samaId: "",
  gender: "Male",
  academicTitle: "",
  medicalDegree: "",
  phoneNumber: "",
};

export const DoctorForm = memo(({ doctor, callback, cancel, update }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupDoctor } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { error } = useNotify();
  const doctorSchema = Yup.object().shape(YupDoctor);
  const [data, setData] = useState<Partial<IClinic>>(initValue);
  const [doctorForm] = Form.useForm();

  useEffect(() => {
    if (doctor) {
      setData({ ...data, ...doctor });
      formRef.current?.setFieldsValue(doctor);
    }
  }, [doctor]);

  const onFinish = async (values: IDoctor) => {
    try {
      dispatch(setLoading(true));
      if (doctor) {
        await update(values);
      } else {
        await callback(values);
        await doctorForm.resetFields();
      }
      await doctorForm.resetFields();
    } catch (err: any) {
      error(t("message.create.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await doctorSchema.validateSyncAt(field, { [field]: value });
    },
  };
  return (
    <Form
      form={doctorForm}
      name="basic"
      ref={formRef}
      initialValues={data}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.name")} name="name" rules={[yupSync]}>
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.gender")} name="gender">
            <Radio.Group>
              {gender.map((item) => (
                <Radio value={item} key={item}>
                  {item}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.samaId")} name="samaId">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.academicTitle")} name="academicTitle">
            <Input type="text" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.medicalDegree")} name="medicalDegree">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.phoneNumber")} name="phoneNumber" rules={[yupSync]}>
            <Input type="number" />
          </Form.Item>
        </Col>
        <Form.Item name="key" hidden={true}>
          <Input type="number" />
        </Form.Item>
      </Row>
      <FormAction cancel={cancel} />
    </Form>
  );
});
